import {FC, useEffect, useMemo, useState} from 'react'
import {
  AlertTriangle,
  Building2,
  GaugeCircle,
  Pencil,
  Plus,
  Search,
  Tags,
  Trash2,
  UserCheck,
  UsersRound,
  X,
} from 'lucide-react'
import {Modal} from 'react-bootstrap'

import * as API from '../../api'
import CompanyModal from './CompanyModal'

const companyInitials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || 'EM'

const getCompanyType = (company: any) => {
  if (company.type_name) return company.type_name
  if (typeof company.company_type === 'string') return company.company_type
  if (typeof company.type === 'string') return company.type
  if (`${company.id_type_company}` === '1') return 'Indústria'
  if (`${company.id_type_company}` === '2') return 'Distribuidor'
  return 'Outro tipo'
}

const Companys: FC = () => {
  const [selectedCompany, setSelectedCompany] = useState<any>(null)
  const [companies, setCompanies] = useState<any[]>([])
  const [showCompanyModal, setShowCompanyModal] = useState(false)
  const [showRemoveModal, setShowRemoveModal] = useState(false)
  const [query, setQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [typeFilter, setTypeFilter] = useState('all')
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [feedback, setFeedback] = useState('')

  async function updateData(showLoading = true) {
    if (showLoading) setLoading(true)
    setLoadError(false)

    try {
      const response = await API.listCompanys()
      setCompanies(Array.isArray(response.data) ? response.data : [])
    } catch {
      setLoadError(true)
    } finally {
      if (showLoading) setLoading(false)
    }
  }

  useEffect(() => {
    updateData()
  }, [])

  const companyTypes = useMemo(
    () => Array.from(new Set(companies.map(getCompanyType))).sort(),
    [companies]
  )

  const filteredCompanies = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase('pt-BR')

    return companies.filter((company) => {
      const active = Boolean(Number(company.is_actived))
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && active) ||
        (statusFilter === 'inactive' && !active)
      const matchesType = typeFilter === 'all' || getCompanyType(company) === typeFilter
      const matchesQuery =
        !normalizedQuery ||
        `${company.name || ''} ${getCompanyType(company)} ${company.guid || ''}`
          .toLocaleLowerCase('pt-BR')
          .includes(normalizedQuery)

      return matchesStatus && matchesType && matchesQuery
    })
  }, [companies, query, statusFilter, typeFilter])

  const activeCompanies = companies.filter((company) => Boolean(Number(company.is_actived))).length
  const totalMembers = companies.reduce(
    (total, company) => total + (Number(company.members) || 0),
    0
  )
  const totalCapacity = companies.reduce(
    (total, company) => total + (Number(company.max_users) || 0),
    0
  )
  const hasFilters = Boolean(query || statusFilter !== 'all' || typeFilter !== 'all')

  function openCreateModal() {
    setSelectedCompany(null)
    setShowCompanyModal(true)
  }

  function openEditModal(company: any) {
    setSelectedCompany(company)
    setShowCompanyModal(true)
  }

  function closeCompanyModal() {
    setShowCompanyModal(false)
    setSelectedCompany(null)
  }

  async function handleSaved(action: 'created' | 'updated') {
    await updateData(false)
    setFeedback(
      action === 'created' ? 'Empresa criada com sucesso.' : 'Empresa atualizada com sucesso.'
    )
    closeCompanyModal()
  }

  async function removeCompany() {
    if (!selectedCompany) return
    setRemoving(true)
    setFeedback('')

    try {
      const response = await API.deleteCompany(selectedCompany.guid)
      if (response?.success === false) throw new Error('Company removal failed')
      await updateData(false)
      setFeedback(`A empresa ${selectedCompany.name} foi removida.`)
      setShowRemoveModal(false)
      setSelectedCompany(null)
    } catch {
      setFeedback('Não foi possível remover a empresa. Tente novamente.')
    } finally {
      setRemoving(false)
    }
  }

  return (
    <section className='ua-section'>
      <Modal
        id='company_form_modal'
        tabIndex={-1}
        dialogClassName='modal-dialog modal-dialog-centered ua-admin-modal-dialog ca-company-modal-dialog'
        show={showCompanyModal}
        onHide={closeCompanyModal}
      >
        <CompanyModal
          companyData={selectedCompany}
          onCompanyCreated={() => handleSaved('created')}
          onCompanyUpdated={() => handleSaved('updated')}
          onClose={closeCompanyModal}
        />
      </Modal>

      <Modal
        id='company_remove_modal'
        tabIndex={-1}
        dialogClassName='modal-dialog modal-dialog-centered mw-450px ua-admin-modal-dialog'
        show={showRemoveModal}
        onHide={() => !removing && setShowRemoveModal(false)}
      >
        <div className='ca-confirm-modal'>
          <div className='ca-confirm-modal__body'>
            <span className='ca-confirm-modal__icon'>
              <AlertTriangle size={22} />
            </span>
            <h3>Remover empresa?</h3>
            <p>
              Você está prestes a remover <strong>{selectedCompany?.name}</strong>. Esta ação não
              pode ser desfeita.
            </p>
          </div>
          <footer className='ca-confirm-modal__footer'>
            <button
              type='button'
              className='ua-secondary-button'
              disabled={removing}
              onClick={() => setShowRemoveModal(false)}
            >
              Cancelar
            </button>
            <button
              type='button'
              className='ua-danger-button'
              disabled={removing}
              onClick={removeCompany}
            >
              {removing ? <span className='ua-spinner' /> : <Trash2 size={14} />}
              Remover empresa
            </button>
          </footer>
        </div>
      </Modal>

      <header className='ua-section__heading'>
        <div className='ua-section__heading-copy'>
          <span className='ua-section__heading-icon'>
            <Building2 size={20} />
          </span>
          <div>
            <h2>Empresas</h2>
            <p>Consulte cadastros, capacidade de usuários, tipo e disponibilidade de acesso.</p>
          </div>
        </div>
        <button type='button' className='ua-primary-button' onClick={openCreateModal}>
          <Plus size={15} /> Adicionar empresa
        </button>
      </header>

      {loading ? (
        <div className='ua-card ua-loading'>
          <span className='ua-spinner' /> Carregando empresas…
        </div>
      ) : loadError ? (
        <div className='ua-empty'>
          <div>
            <span className='ua-empty__icon'>
              <Building2 size={22} />
            </span>
            <h3>Não foi possível carregar as empresas</h3>
            <p>Tente novamente para acessar os cadastros.</p>
            <button type='button' className='ua-secondary-button mt-4' onClick={() => updateData()}>
              Tentar novamente
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className='ua-stats'>
            <div className='ua-stat'>
              <span className='ua-stat__icon'>
                <Building2 size={18} />
              </span>
              <span>
                <strong className='ua-stat__value'>{companies.length}</strong>
                <small className='ua-stat__label'>Empresas cadastradas</small>
              </span>
            </div>
            <div className='ua-stat'>
              <span className='ua-stat__icon'>
                <UserCheck size={18} />
              </span>
              <span>
                <strong className='ua-stat__value'>{activeCompanies}</strong>
                <small className='ua-stat__label'>Empresas ativas</small>
              </span>
            </div>
            <div className='ua-stat'>
              <span className='ua-stat__icon'>
                <UsersRound size={18} />
              </span>
              <span>
                <strong className='ua-stat__value'>{totalMembers}</strong>
                <small className='ua-stat__label'>Usuários vinculados</small>
              </span>
            </div>
            <div className='ua-stat'>
              <span className='ua-stat__icon'>
                <GaugeCircle size={18} />
              </span>
              <span>
                <strong className='ua-stat__value'>{totalCapacity || '—'}</strong>
                <small className='ua-stat__label'>Capacidade contratada</small>
              </span>
            </div>
          </div>

          <div className='ua-toolbar'>
            <div className='ua-field ua-field--wide'>
              <label htmlFor='company-search'>Buscar empresa</label>
              <div className='ua-search'>
                <Search size={15} />
                <input
                  id='company-search'
                  value={query}
                  placeholder='Nome, tipo ou identificador'
                  onChange={(event) => setQuery(event.target.value)}
                />
                {query && (
                  <button
                    type='button'
                    className='ua-search__clear'
                    aria-label='Limpar busca'
                    onClick={() => setQuery('')}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>
            <div className='ua-field'>
              <label htmlFor='company-type-filter'>Tipo</label>
              <div className='ua-select'>
                <select
                  id='company-type-filter'
                  value={typeFilter}
                  onChange={(event) => setTypeFilter(event.target.value)}
                >
                  <option value='all'>Todos os tipos</option>
                  {companyTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className='ua-field'>
              <label htmlFor='company-status-filter'>Status</label>
              <div className='ua-select'>
                <select
                  id='company-status-filter'
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                >
                  <option value='all'>Todos</option>
                  <option value='active'>Ativas</option>
                  <option value='inactive'>Inativas</option>
                </select>
              </div>
            </div>
            {hasFilters && (
              <button
                type='button'
                className='ua-secondary-button'
                onClick={() => {
                  setQuery('')
                  setTypeFilter('all')
                  setStatusFilter('all')
                }}
              >
                <X size={14} /> Limpar
              </button>
            )}
          </div>

          {feedback && (
            <div
              className={`ua-feedback ${
                feedback.startsWith('Não foi possível') ? 'ua-feedback--danger' : ''
              }`}
            >
              {feedback}
            </div>
          )}

          <div className='ua-card'>
            <div className='ua-card__header'>
              <div>
                <h3>Lista de empresas</h3>
                <p>{filteredCompanies.length} resultado(s) encontrado(s)</p>
              </div>
            </div>

            {filteredCompanies.length ? (
              <div className='ua-table-wrap'>
                <table className='ua-table'>
                  <thead>
                    <tr>
                      <th>Empresa</th>
                      <th>Tipo</th>
                      <th>Membros</th>
                      <th>Capacidade</th>
                      <th>Status</th>
                      <th className='text-end'>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCompanies.map((company) => (
                      <tr key={company.guid}>
                        <td>
                          <span className='ua-user-cell'>
                            <span className='ua-avatar'>{companyInitials(company.name)}</span>
                            <span className='ua-user-cell__copy'>
                              <strong>{company.name}</strong>
                              <small className='ca-company-secondary'>ID {company.guid}</small>
                            </span>
                          </span>
                        </td>
                        <td>
                          <span className='ca-company-type'>
                            <Tags size={13} /> {getCompanyType(company)}
                          </span>
                        </td>
                        <td>{Number(company.members) || 0}</td>
                        <td>{Number(company.max_users) || '—'}</td>
                        <td>
                          <span
                            className={`ua-badge ${
                              Boolean(Number(company.is_actived))
                                ? 'ua-badge--success'
                                : 'ua-badge--danger'
                            }`}
                          >
                            {Boolean(Number(company.is_actived)) ? 'Ativa' : 'Inativa'}
                          </span>
                        </td>
                        <td>
                          <div className='ua-actions'>
                            <button
                              type='button'
                              className='ua-icon-button'
                              title={`Editar ${company.name}`}
                              aria-label={`Editar ${company.name}`}
                              onClick={() => openEditModal(company)}
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              type='button'
                              className='ua-icon-button ua-icon-button--danger'
                              title={`Remover ${company.name}`}
                              aria-label={`Remover ${company.name}`}
                              onClick={() => {
                                setSelectedCompany(company)
                                setShowRemoveModal(true)
                              }}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className='ua-empty ua-empty--inside'>
                <div>
                  <span className='ua-empty__icon'>
                    <Search size={22} />
                  </span>
                  <h3>Nenhuma empresa encontrada</h3>
                  <p>Ajuste os filtros ou adicione uma nova empresa.</p>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </section>
  )
}

export default Companys
